import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';

/**
 * Uso de la plataforma: qué está consumiendo la infraestructura.
 *
 * Junta cuatro fuentes que antes había que mirar en cuatro sitios distintos:
 * el VPS por la API de Hostinger, la base de datos por information_schema, las
 * instancias de WhatsApp por Evolution y el propio proceso del backend.
 *
 * Cada bloque se dibuja aunque los demás fallen, y el que no tiene datos dice
 * qué le falta en vez de quedarse vacío.
 */

const VERDE = '#16a34a';
const AMBAR = '#d97706';
const ROJO  = '#dc2626';

const color = (pct) => (pct == null ? 'var(--color-text-muted)' : pct >= 85 ? ROJO : pct >= 50 ? AMBAR : VERDE);
const pct = (usado, total) => (total ? Math.min(Math.round((usado / total) * 1000) / 10, 100) : null);
const mb = (n) => `${Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 })} MB`;

function Barra({ valor }) {
  return (
    <div style={{ height: 7, background: 'var(--color-surface-2)', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{
        width: `${valor == null ? 0 : valor}%`, height: '100%',
        background: color(valor), borderRadius: 99, transition: 'width .4s ease',
      }} />
    </div>
  );
}

function Medidor({ titulo, detalle, usado, total, unidad, porcentaje }) {
  const p = porcentaje != null ? porcentaje : pct(usado, total);
  return (
    <div style={{ padding: '0.7rem 0', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{titulo}</div>
          {detalle && <div style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)' }}>{detalle}</div>}
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: color(p) }}>
            {p == null ? '—' : `${p}%`}
          </div>
          {total != null && (
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
              {Number(usado || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 })} de{' '}
              {Number(total).toLocaleString('es-CO')} {unidad}
            </div>
          )}
        </div>
      </div>
      <Barra valor={p} />
    </div>
  );
}

/**
 * Medidor de una métrica del VPS. La CPU llega ya en porcentaje; la memoria y
 * el disco llegan en MB y traen su total, así que se muestran las dos cosas:
 * el porcentaje manda en la barra y el valor absoluto va debajo, que es lo que
 * sirve para decidir si hay que ampliar.
 */
function MedidorVps({ titulo, detalle, m, usarActual }) {
  if (!m) return <Medidor titulo={titulo} detalle={detalle} porcentaje={null} />;

  const p = usarActual ? (m.porcentaje_max ?? m.porcentaje) : m.porcentaje;
  const valor = usarActual ? m.actual : m.promedio;
  const enMb = m.unidad === 'MB';
  // Sin total conocido se muestra solo lo usado: "5.887 de 0 MB" no es un dato,
  // es un hueco disfrazado de dato.
  const abs = enMb
    ? (m.total
        ? `${Number(valor).toLocaleString('es-CO')} de ${Number(m.total).toLocaleString('es-CO')} MB`
        : `${Number(valor).toLocaleString('es-CO')} MB usados`)
    : null;

  return (
    <div style={{ padding: '0.7rem 0', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{titulo}</div>
          <div style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)' }}>{detalle}</div>
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: color(p) }}>
            {p == null ? '—' : `${p}%`}
          </div>
          {abs
            ? <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{abs}</div>
            : (!enMb && valor != null && (
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{valor} %</div>
              ))}
        </div>
      </div>
      <Barra valor={p} />
    </div>
  );
}

function Tarjeta({ titulo, nota, children, accion }) {
  return (
    <div className="card" style={{ padding: '1rem 1.15rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: nota ? 2 : 10 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>{titulo}</h3>
        {accion}
      </div>
      {nota && <p style={{ margin: '0 0 12px', fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{nota}</p>}
      {children}
    </div>
  );
}

// Bloque sin datos: siempre dice qué falta para tenerlos.
function SinDatos({ motivo }) {
  return (
    <div style={{
      padding: '1rem', borderRadius: 8, fontSize: '0.82rem', lineHeight: 1.55,
      background: 'var(--color-warning-bg)', color: 'var(--color-warning)',
    }}>
      ⚠ {motivo}
    </div>
  );
}

export default function UsagePage() {
  const esMovil = useIsMobile(900);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get('/infra/uso');
      setDatos(r.data);
      setError('');
    } catch (err) {
      setError(err.response?.status === 404
        ? 'El servidor todavía no tiene este módulo: falta subir routes/infra.js y services/infra.js y reiniciar.'
        : (err.response?.data?.error || 'No se pudo leer el consumo'));
      setDatos(null);
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando && !datos) {
    return <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Consultando servidores...</div>;
  }

  if (error) {
    return (
      <div>
        <h1 className="page-title">Uso de la plataforma</h1>
        <div style={{ marginTop: '1rem' }}><SinDatos motivo={error} /></div>
        <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const { vps, base_datos: bd, whatsapp: wa, proceso, generado } = datos || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>Uso de la plataforma</h1>
          <p style={{ margin: 0, fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>
            Servidores, base de datos y sesiones de WhatsApp
            {generado && ` · leído ${new Date(generado).toLocaleString('es-CO')}`}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={cargar} disabled={cargando}>
          {cargando ? 'Actualizando...' : '↻ Actualizar'}
        </button>
      </div>

      <div style={{
        display: 'grid', gap: '1rem', marginTop: '1.25rem',
        gridTemplateColumns: esMovil ? '1fr' : 'repeat(2, minmax(0,1fr))',
      }}>

        {/* WhatsApp primero: es el límite que manda. Cada negocio vinculado
            ocupa memoria del VPS de forma permanente. */}
        <Tarjeta
          titulo="Negocios con WhatsApp"
          nota="Es el límite real de la plataforma: cada sesión vinculada ocupa memoria del VPS mande o no mensajes."
        >
          {!wa?.disponible ? (
            <SinDatos motivo={wa?.motivo || wa?.error || 'Evolution no respondió'} />
          ) : (
            <>
              <Medidor
                titulo="Sesiones conectadas"
                detalle={`${wa.total} instancia${wa.total === 1 ? '' : 's'} creada${wa.total === 1 ? '' : 's'} en el servidor`}
                usado={wa.conectadas} total={wa.limite} unidad="estimadas"
                porcentaje={wa.porcentaje}
              />
              <p style={{ margin: '12px 0 0', fontSize: '0.76rem', color: 'var(--color-text-muted)', lineHeight: 1.55 }}>
                Al llegar a {wa.limite} hay que subir el VPS a un plan mayor. El tope es una
                estimación del tamaño del servidor, ajustable con <code>LIMITE_INSTANCIAS</code>.
              </p>
            </>
          )}
        </Tarjeta>

        {/* VPS */}
        <Tarjeta titulo="Servidores VPS" nota="Datos de la API de Hostinger, promedio de las últimas 24 horas.">
          {!vps?.disponible ? (
            <SinDatos motivo={vps?.motivo || vps?.error || 'Sin datos del VPS'} />
          ) : vps.servidores.map(s => (
            <div key={s.id} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <strong style={{ fontSize: '0.85rem' }}>{s.nombre}</strong>
                <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                  {[s.vcpu && `${s.vcpu} vCPU`, s.ram_mb && `${Math.round(s.ram_mb / 1024)} GB`, s.estado]
                    .filter(Boolean).join(' · ')}
                </span>
              </div>
              {s.fallo ? <SinDatos motivo={`Métricas no disponibles: ${s.fallo}`} /> : (
                <>
                  <MedidorVps titulo="CPU" m={s.metricas?.cpu}
                              detalle={`Promedio 24 h · pico ${s.metricas?.cpu?.porcentaje_max ?? '—'} %`} />
                  <MedidorVps titulo="Memoria" m={s.metricas?.ram} detalle="Promedio 24 h" />
                  <MedidorVps titulo="Disco" m={s.metricas?.disco} detalle="Último valor" usarActual />
                </>
              )}
            </div>
          ))}
        </Tarjeta>

        {/* Base de datos */}
        <Tarjeta
          titulo="Base de datos"
          nota={bd?.total_mb != null
            ? `${bd.total_mb} MB en ${bd.tablas?.length || 0} tablas · ${Number(bd.total_filas || 0).toLocaleString('es-CO')} filas`
            : null}
        >
          {bd?.error ? <SinDatos motivo={bd.error} /> : (
            <>
              {bd.pool && (
                <Medidor
                  titulo="Conexiones de la aplicación"
                  detalle={bd.pool.en_uso != null
                    ? `${bd.pool.en_uso} en uso, ${bd.pool.libres} libres`
                    : 'Abiertas por el pool'}
                  usado={bd.pool.abiertas} total={bd.pool.limite} unidad="del pool" />
              )}
              {bd.servidor_conexiones != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '0.7rem 0', fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>
                    Servidor MySQL compartido
                    <span style={{ display: 'block', fontSize: '0.72rem' }}>Incluye otros inquilinos, no es consumo tuyo</span>
                  </span>
                  <strong style={{ whiteSpace: 'nowrap' }}>{bd.servidor_conexiones} conexiones</strong>
                </div>
              )}
              <div style={{ overflowX: 'auto', marginTop: 12 }}>
                <table className="table" style={{ width: '100%', minWidth: 320 }}>
                  <thead>
                    <tr>
                      <th>Tabla más pesada</th>
                      <th style={{ textAlign: 'right' }}>Filas</th>
                      <th style={{ textAlign: 'right' }}>Tamaño</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bd.tablas || []).slice(0, 8).map(t => (
                      <tr key={t.tabla}>
                        <td style={{ fontSize: '0.8rem' }}>{t.tabla}</td>
                        <td style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                          {t.filas.toLocaleString('es-CO')}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: '0.8rem', fontWeight: 600 }}>{t.mb} MB</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Tarjeta>

        {/* Proceso del backend */}
        <Tarjeta
          titulo="Proceso del backend"
          nota="El hosting compartido no expone métricas por API: esto es lo único medible desde dentro."
        >
          {proceso?.error ? <SinDatos motivo={proceso.error} /> : (
            <>
              <Medidor titulo="Memoria del proceso" detalle={`Heap ${mb(proceso.heap_mb)}`}
                       usado={proceso.memoria_mb} total={proceso.limite_memoria_mb} unidad="MB del plan" />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '0.7rem 0', fontSize: '0.82rem' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>En pie desde</span>
                <strong>{proceso.en_pie_horas} h</strong>
              </div>
              {proceso.en_pie_horas < 1 && (
                <p style={{ margin: 0, fontSize: '0.76rem', color: AMBAR, lineHeight: 1.55 }}>
                  Lleva menos de una hora encendido. Si este número se reinicia solo, el proceso se
                  está durmiendo entre visitas y por eso las tareas programadas necesitan el cron.
                </p>
              )}
              <div style={{ paddingTop: 8, fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                Node {proceso.node}
              </div>
            </>
          )}
        </Tarjeta>

      </div>
    </div>
  );
}
